import { useEffect } from 'react';

import { getBrazeInstance, isWeb } from '@lib/analytics/braze';
import { getBrazeSignature, installSdkAuthErrorHandler } from '@lib/analytics/brazeAuth';
import { syncBrazeProfile } from '@lib/analytics/brazeProfile';
import useAuth from '@lib/hooks/useAuth';
import { Order } from '@lib/entities';
import { useMenu } from './useMenu';

const useBraze = () => {
    const { user, pendingMfa } = useAuth();
    const { categories } = useMenu();

    const logEvent = async (eventName: string, properties?: Record<string, any>) => {
        if (!user) {
            return;
        }

        const defaultProperties = {
            user_id: user.id,
            platform: isWeb ? 'web' : 'native'
        };

        const braze = await getBrazeInstance();
        if (braze) {
            braze.logCustomEvent(eventName, { ...defaultProperties, ...properties });
        }
    };

    const logPurchase = async (
        productId: string,
        price: number,
        currency: string = 'USD',
        quantity: number = 1,
        properties?: Record<string, any>
    ) => {
        if (!user) {
            return;
        }

        const defaultProperties = {
            user_id: user.id,
            platform: isWeb ? 'web' : 'native',
            ...properties
        };

        const braze = await getBrazeInstance();
        if (braze) {
            const priceParam = price.toString();
            braze.logPurchase(productId, priceParam, currency, quantity, defaultProperties);
        }
    };

    const logOrderPurchase = async (order: Order) => {
        if (!user) {
            return;
        }

        order.items.forEach((item) => {
            const category = categories.find((c) => c.id === Number(item.category));
            logPurchase(item.menuitem_id.toString(), item.item_subtotal, 'USD', item.quantity, {
                orderId: order.order_id,
                categoryId: item.category,
                categoryName: category?.type,
                storeId: order.store_id,
                storeName: order.store_name
            });
        });
    };

    useEffect(() => {
        const initializeBraze = async () => {
            if (!user || !user.id) {
                return;
            }

            // A pending-MFA user carries a real id but no session bearer yet, so the
            // signature request would be a guaranteed 401. Defer until MFA completes.
            if (pendingMfa) {
                return;
            }

            const braze = await getBrazeInstance();
            if (braze) {
                installSdkAuthErrorHandler(braze);

                const userId = `${user.id}`;
                // JSAD-7145. An undefined signature is a supported outcome: changeUser
                // then behaves exactly as it did before, so a failed token request
                // degrades messaging quietly instead of blocking the app.
                const signature = await getBrazeSignature(userId);

                braze.changeUser(userId, signature);
                // JSAD-7144. Attributes are keyed on the SDK's CURRENT user id when they
                // are enqueued, so this must follow changeUser; openSession() below flushes
                // the queue at once, so it must come before it. Synchronous on purpose:
                // no await between the calls.
                syncBrazeProfile(braze, user);
                // Only web SDK has openSession method
                if (isWeb && braze.openSession) {
                    braze.openSession();
                }
            }
        };

        initializeBraze();
    }, [user, pendingMfa]);

    return {
        logEvent,
        logPurchase,
        logOrderPurchase
    };
};

export default useBraze;
